import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { authApi } from '@/features/auth'
import { useSessionStore } from '@/shared/session'
import { decide } from './guards'

declare module 'vue-router' {
  interface RouteMeta {
    guestOnly?: boolean
    requiresAuth?: boolean
    admin?: boolean
    /** Заголовок для заглушки раздела из следующих задач. */
    titleKey?: string
    stage?: string
  }
}

/** Маршруты ТЗ 18.3. Разделы следующих задач пока открывают заглушку. */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/LoginPage.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/login/mfa',
    name: 'login-mfa',
    component: () => import('@/features/auth/LoginMfaPage.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/setup',
    name: 'setup',
    component: () => import('@/features/auth/SetupPage.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/password/forgot',
    name: 'password-forgot',
    component: () => import('@/features/auth/ForgotPasswordPage.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/password/reset/:token',
    name: 'password-reset',
    component: () => import('@/features/auth/ResetPasswordPage.vue'),
    props: true,
    meta: { guestOnly: true },
  },
  {
    path: '/invite/:token',
    name: 'invite',
    component: () => import('@/features/auth/InvitePage.vue'),
    props: true,
  },
  {
    path: '/',
    component: () => import('./shell/AppShell.vue'),
    meta: { requiresAuth: true },
    children: [
      // Главной нет: приложение открывается каталогом (решение владельца 7 октября 2026).
      { path: '', name: 'home', redirect: (to) => ({ path: '/books', query: to.query }) },
      {
        path: 'books',
        name: 'books',
        component: () => import('@/features/catalog/CatalogPage.vue'),
      },
      {
        path: 'books/new',
        name: 'book-new',
        component: () => import('@/features/catalog/BookFormPage.vue'),
      },
      {
        path: 'books/:id',
        name: 'book',
        component: () => import('@/features/catalog/BookPage.vue'),
        props: true,
      },
      {
        path: 'books/:id/edit',
        name: 'book-edit',
        component: () => import('@/features/catalog/BookFormPage.vue'),
        props: true,
      },
      {
        path: 'shelf',
        name: 'shelf',
        component: () => import('@/features/reading/ShelfPage.vue'),
      },
      {
        path: 'add',
        name: 'add',
        component: () => import('@/features/adding/AddPage.vue'),
      },
      {
        path: 'add/scan',
        name: 'add-scan',
        component: () => import('@/features/adding/ScanPage.vue'),
      },
      {
        path: 'add/batch',
        name: 'add-batch',
        component: () => import('@/features/adding/BatchPage.vue'),
      },
      {
        path: 'review',
        name: 'review',
        component: () => import('@/features/adding/ReviewPage.vue'),
      },
      {
        path: 'loans',
        name: 'loans',
        component: () => import('@/features/lending/LoansPage.vue'),
      },
      {
        path: 'contacts',
        name: 'contacts',
        component: () => import('@/features/lending/ContactsPage.vue'),
      },
      {
        path: 'contacts/:id',
        name: 'contact',
        component: () => import('@/features/lending/ContactPage.vue'),
        props: true,
      },
      { path: 'more', name: 'more', component: () => import('./MorePage.vue') },
      {
        path: 'libraries',
        name: 'libraries',
        component: () => import('@/features/libraries/LibrariesPage.vue'),
      },
      {
        path: 'libraries/:id',
        name: 'library',
        component: () => import('@/features/libraries/LibraryPage.vue'),
        props: true,
      },
      {
        path: 'settings/profile',
        name: 'settings-profile',
        component: () => import('@/features/settings/ProfilePage.vue'),
      },
      {
        path: 'settings/notifications',
        name: 'settings-notifications',
        component: () => import('@/features/settings/NotificationsPage.vue'),
      },
      {
        path: 'settings/security',
        name: 'settings-security',
        component: () => import('@/features/settings/SecurityPage.vue'),
      },
      {
        path: 'settings/data',
        name: 'settings-data',
        component: () => import('@/features/data/DataPage.vue'),
      },
      {
        path: 'settings/trash',
        name: 'settings-trash',
        component: () => import('@/features/catalog/TrashPage.vue'),
      },
      { path: 'admin', redirect: '/admin/users' },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/features/admin/AdminUsersPage.vue'),
        meta: { admin: true },
      },
      {
        path: 'admin/settings',
        name: 'admin-settings',
        component: () => import('@/features/admin/AdminSettingsPage.vue'),
        meta: { admin: true },
      },
      { path: 'forbidden', name: 'forbidden', component: () => import('./ForbiddenPage.vue') },
    ],
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('./NotFoundPage.vue') },
]

/**
 * Позиция при возврате «Назад» (ТЗ 14.4 п. 4): список из кэша рисуется не мгновенно,
 * поэтому ждём, пока страница дорастёт до сохранённой позиции (не дольше 1,5 с).
 */
function whenTallEnough(top: number): Promise<void> {
  const started = performance.now()
  return new Promise((resolve) => {
    const check = (): void => {
      const enough = document.documentElement.scrollHeight >= top + window.innerHeight
      if (enough || performance.now() - started > 1500) resolve()
      else requestAnimationFrame(check)
    }
    check()
  })
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: async (to, from, saved) => {
    if (saved) {
      await whenTallEnough(saved.top)
      return saved
    }
    // Смена фильтров, поиска или вкладки на том же экране не прыгает наверх.
    if (to.path === from.path) return false
    return { top: 0 }
  },
})

let setupCompleted: boolean | null = null

router.beforeEach(async (to) => {
  const session = useSessionStore()
  await session.ensure()
  await session.checkMfaPolicy()
  // Первый запуск проверяется, пока сервер не подтвердил, что он выполнен.
  if (!session.isAuthenticated && setupCompleted !== true) {
    setupCompleted = await authApi
      .setupStatus()
      .then((s) => s.completed)
      .catch(() => null)
  }
  return decide(to, {
    isAuthenticated: session.isAuthenticated,
    isAdmin: session.isAdmin,
    mfaSetupRequired: session.mfaSetupRequired,
    setupCompleted: session.isAuthenticated ? true : setupCompleted,
    offline: session.isUnverified,
  })
})
